import { useFetcher, useLocation } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

type WantToGoButtonProps = {
	restaurantId: string
	initialIsWantToGo?: boolean
	className?: string
	size?: 'sm' | 'md' | 'lg'
}

export function WantToGoButton({
	restaurantId,
	initialIsWantToGo = false,
	className,
	size = 'md',
}: WantToGoButtonProps) {
	const user = useOptionalUser()
	const fetcher = useFetcher<{ isWantToGo: boolean }>()
	const location = useLocation()

	// Optimistic UI: use fetcher data if available, otherwise use initial
	const isWantToGo = fetcher.data?.isWantToGo ?? initialIsWantToGo
	const isLoading = fetcher.state !== 'idle'

	const sizeClasses = {
		sm: 'size-8',
		md: 'size-10',
		lg: 'size-12',
	}

	const iconSizes = {
		sm: 'size-4',
		md: 'size-5',
		lg: 'size-6',
	}

	if (!user) {
		// Not logged in - link to login
		return (
			<a
				href={`/login?redirectTo=${encodeURIComponent(location.pathname)}`}
				className={cn(
					'flex items-center justify-center rounded-full bg-[#1a1a2e] border border-[#2a2a3e] text-[#b0b0e0] hover:text-[#00e0ff] hover:border-[#00e0ff] transition-colors',
					sizeClasses[size],
					className,
				)}
				title="Log in to save to your list"
			>
				<HeartOutline className={iconSizes[size]} />
			</a>
		)
	}

	return (
		<fetcher.Form method="POST" action="/resources/want-to-go">
			<input type="hidden" name="restaurantId" value={restaurantId} />
			<button
				type="submit"
				disabled={isLoading}
				className={cn(
					'flex items-center justify-center rounded-full transition-all',
					sizeClasses[size],
					isWantToGo
						? 'bg-[#ff4757]/20 border border-[#ff4757] text-[#ff4757] hover:bg-[#ff4757]/30'
						: 'bg-[#1a1a2e] border border-[#2a2a3e] text-[#b0b0e0] hover:text-[#ff4757] hover:border-[#ff4757]',
					isLoading && 'opacity-50 cursor-wait',
					className,
				)}
				title={isWantToGo ? 'Remove from want-to-go list' : 'Add to want-to-go list'}
			>
				{isWantToGo ? (
					<HeartFilled className={iconSizes[size]} />
				) : (
					<HeartOutline className={iconSizes[size]} />
				)}
			</button>
		</fetcher.Form>
	)
}

function HeartOutline({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
		</svg>
	)
}

function HeartFilled({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			fill="currentColor"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
		</svg>
	)
}
